/**
 * Files on an email: how many, and how large.
 *
 * Jonathon, 2026-09-10: "when emailing inside of roofboard there needs to be
 * the option of attaching items." The Edge Function always could carry files —
 * the storm history report goes as one — but nothing a rep could reach offered
 * to. Decided here once so the composer refuses what the server would, in the
 * same words, before anyone has waited for an upload. `gmail-send/message.ts`
 * carries the same two numbers, pinned together by
 * supabase/test/gmail-message.test.ts.
 *
 * **Why 15 MB.** Gmail refuses a message over 25 MB and counts it encoded,
 * which is a third larger than the files; 15 MB of files is about 20 MB sent.
 * Phone photos are shrunk in the browser before they count, so this is a limit
 * a rep meets with a PDF, not with pictures of a roof.
 */

export const MAX_EMAIL_ATTACHMENTS = 10;
export const MAX_EMAIL_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/** "340 KB", "2.4 MB" — what a person reads a file size as. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Why these files cannot go on one email, or null when they can.
 *
 * Says what to do about it, because the person reading it is standing in a
 * driveway with a customer's roof on their phone.
 */
export function attachmentRefusal(sizes: readonly number[]): string | null {
  if (sizes.length > MAX_EMAIL_ATTACHMENTS) {
    return (
      `One email can carry ${MAX_EMAIL_ATTACHMENTS} files. ` +
      "Take some off, or send the rest in a second email."
    );
  }
  const total = sizes.reduce((sum, size) => sum + Math.max(0, size), 0);
  if (total > MAX_EMAIL_ATTACHMENT_BYTES) {
    return (
      `Those files come to ${formatFileSize(total)} and one email can carry ` +
      `${formatFileSize(MAX_EMAIL_ATTACHMENT_BYTES)}. Take one off, or send ` +
      "the rest in a second email."
    );
  }
  return null;
}
